import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search, X, AlertCircle } from 'lucide-react';
import {
  AggregatedEnrichmentSummary,
  EnrichmentDetail,
  ProductEnrichment,
  getEnrichment,
  listEnrichmentsAcrossDistributors,
} from '../../services/distributorService';

/**
 * Catalog browse — cross-distributor view of all approved (Active)
 * enrichments for the partner. Closes the demo arc: today the user can
 * approve an enrichment but the *outcome* (a real product they could put
 * in front of customers) wasn't visible anywhere. This page is the
 * partner's view of their published catalog.
 *
 * Data flow: list rows from each connected distributor in parallel
 * (per-distributor failures are non-fatal), then fetch the full
 * enrichment payload for each row in a small concurrency-limited pool
 * so we can render canonical name + category in the table without an
 * extra round-trip per row click.
 *
 * Click row → readonly side-panel detail. Edit / approve / reject etc.
 * still happen on the per-distributor tab (link out from the panel).
 */

// Distributors we currently have connectors for. Keep this in sync with the
// keyed-DI registrations in the marketplace function. Adding a new
// distributor here surfaces it in the catalog browse without further work.
const KNOWN_DISTRIBUTORS: Array<{ id: string; label: string }> = [
  { id: 'ingram', label: 'Ingram Micro' },
  { id: 'dickerdata', label: 'Dicker Data' },
  { id: 'telstra', label: 'Telstra' },
];

const ENRICHMENT_DETAIL_CONCURRENCY = 5;

interface CatalogRow {
  distributorId: string;
  distributorLabel: string;
  sku: string;
  enrichedAt: string;
  agentVersion: string | null;
  // Hydrated from the enrichment blob — null until the second-stage fetch lands.
  canonicalName: string | null;
  vendor: string | null;
  productType: string | null;
  categoryPrimary: string | null;
  categorySecondary: string | null;
  confidence: string | null;
  marketingSummary: string | null;
  imageUrl: string | null;
  enrichment: ProductEnrichment | null;
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  fn: (item: T, idx: number) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const idx = cursor++;
      try {
        await fn(items[idx], idx);
      } catch {
        // Per-item failures are tolerated; the row stays in its summary-only state.
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, worker),
  );
}

export function CatalogBrowseView() {
  const [rows, setRows] = useState<CatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<Array<{ distributorId: string; message: string }>>([]);
  const [search, setSearch] = useState('');
  const [filterDistributor, setFilterDistributor] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [openRow, setOpenRow] = useState<CatalogRow | null>(null);

  const labelFor = useCallback((id: string) => {
    return KNOWN_DISTRIBUTORS.find((d) => d.id === id)?.label ?? id;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFailures([]);
    try {
      const { items, failures: f } = await listEnrichmentsAcrossDistributors(
        KNOWN_DISTRIBUTORS.map((d) => d.id),
        'Active',
      );
      setFailures(f);

      // Project summaries into rows immediately so the user sees a count + SKU
      // list while the per-row detail fetches kick in.
      const initial: CatalogRow[] = items.map((s: AggregatedEnrichmentSummary) => ({
        distributorId: s.distributorId,
        distributorLabel: labelFor(s.distributorId),
        sku: s.sku,
        enrichedAt: s.enrichedAt,
        agentVersion: s.agentVersion,
        canonicalName: null,
        vendor: null,
        productType: null,
        categoryPrimary: null,
        categorySecondary: null,
        confidence: null,
        marketingSummary: null,
        imageUrl: null,
        enrichment: null,
      }));
      setRows(initial);
      setLoading(false);
      setHydrating(initial.length > 0);

      // Stage 2: hydrate detail per row with bounded concurrency.
      const hydrated: CatalogRow[] = [...initial];
      await runWithConcurrency(initial, ENRICHMENT_DETAIL_CONCURRENCY, async (row, idx) => {
        const detail: EnrichmentDetail = await getEnrichment(row.distributorId, row.sku);
        const e = detail.enrichment;
        hydrated[idx] = {
          ...row,
          canonicalName: e?.canonicalName ?? null,
          // Vendor isn't a field on ProductEnrichment — it lives on the underlying
          // distributor product. Best-effort: pull from the canonical name's
          // first token. Worth a backend enrichment-row extension later.
          vendor: e?.canonicalName?.split(' ')[0] ?? null,
          productType: e?.productType ?? null,
          categoryPrimary: e?.category?.primary ?? null,
          categorySecondary: e?.category?.secondary ?? null,
          confidence: typeof e?.confidence === 'string' ? e.confidence : null,
          marketingSummary: e?.marketingSummary ?? null,
          imageUrl: e?.imageUrl ?? null,
          enrichment: e ?? null,
        };
        // React state update on each completion — gives a "filling in" feel.
        setRows([...hydrated]);
      });
      setHydrating(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load catalog');
      setLoading(false);
      setHydrating(false);
    }
  }, [labelFor]);

  useEffect(() => {
    load();
  }, [load]);

  const distinctCategories = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (r.categoryPrimary) set.add(r.categoryPrimary);
    });
    return Array.from(set).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterDistributor !== 'all' && r.distributorId !== filterDistributor) return false;
      if (filterCategory !== 'all' && r.categoryPrimary !== filterCategory) return false;
      if (!needle) return true;
      const hay = [r.sku, r.canonicalName, r.vendor, r.productType, r.categoryPrimary]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, search, filterDistributor, filterCategory]);

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-slate-900">Catalog</h2>
          <p className="text-sm text-slate-500 mt-1">
            All approved products across your connected distributors. This is what
            customers see in your storefront.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {failures.length > 0 && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 inline-flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-px flex-shrink-0" />
            <div>
              Couldn't load from {failures.length} distributor{failures.length === 1 ? '' : 's'}:
              <ul className="mt-1 ml-5 list-disc">
                {failures.map((f) => (
                  <li key={f.distributorId}>
                    <span className="font-mono">{labelFor(f.distributorId)}</span> — {f.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SKU, name, vendor…"
              className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterDistributor}
            onChange={(e) => setFilterDistributor(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          >
            <option value="all">All distributors</option>
            {KNOWN_DISTRIBUTORS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            disabled={distinctCategories.length === 0}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white disabled:bg-slate-50 disabled:text-slate-400"
          >
            <option value="all">All categories</option>
            {distinctCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <div className="flex-1" />
          <div className="text-xs text-slate-500 inline-flex items-center gap-2">
            {hydrating && <Loader2 className="h-3 w-3 animate-spin" />}
            {visibleRows.length} of {rows.length} product{rows.length === 1 ? '' : 's'}
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading catalog…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <p className="text-sm text-slate-600">
              No approved products yet. Go to a distributor tab, add SKUs, run a sync,
              and approve drafts to populate this view.
            </p>
          </div>
        ) : visibleRows.length === 0 ? (
          <p className="text-sm text-slate-500">
            No products match the current filters.{' '}
            <button
              onClick={() => {
                setSearch('');
                setFilterDistributor('all');
                setFilterCategory('all');
              }}
              className="inline-flex underline text-slate-700 hover:text-slate-900"
            >
              Clear filters
            </button>
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left font-medium py-2.5 px-3 w-12"></th>
                  <th className="text-left font-medium py-2.5 px-3">Source</th>
                  <th className="text-left font-medium py-2.5 px-3">SKU</th>
                  <th className="text-left font-medium py-2.5 px-3">Product</th>
                  <th className="text-left font-medium py-2.5 px-3">Category</th>
                  <th className="text-left font-medium py-2.5 px-3">Confidence</th>
                  <th className="text-left font-medium py-2.5 px-3">Approved</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.map((row) => (
                  <tr
                    key={`${row.distributorId}/${row.sku}`}
                    onClick={() => setOpenRow(row)}
                    className="hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="py-2 px-3">
                      <ProductThumb row={row} size="sm" />
                    </td>
                    <td className="py-2.5 px-3 text-xs">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                        {row.distributorLabel}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 font-mono text-xs text-slate-900">
                      {row.sku}
                    </td>
                    <td className="py-2.5 px-3 text-slate-900">
                      {row.canonicalName ? (
                        <>
                          <div className="font-medium truncate max-w-md">
                            {row.canonicalName}
                          </div>
                          {row.productType && (
                            <div className="text-[11px] text-slate-500">{row.productType}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400 text-xs italic">Loading…</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-slate-600 text-xs">
                      {row.categoryPrimary ?? '—'}
                      {row.categorySecondary && (
                        <span className="text-slate-400"> · {row.categorySecondary}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-xs">
                      {row.confidence ? (
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            row.confidence === 'high'
                              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                              : row.confidence === 'medium'
                              ? 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200'
                              : 'bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200'
                          }`}
                        >
                          {row.confidence}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-slate-500">
                      {new Date(row.enrichedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openRow && (
        <CatalogDetailDrawer row={openRow} onClose={() => setOpenRow(null)} />
      )}
    </div>
  );
}

/**
 * Product image thumb with graceful fallback. Two sizes — `sm` for the table
 * row, `lg` for the drawer hero. Falls back to a vendor-coloured initials
 * block when no URL is set or the image fails to load. The fallback hashes
 * the SKU into a stable colour so successive renders for the same product
 * pick the same swatch (looks intentional, not random).
 */
function ProductThumb({ row, size }: { row: CatalogRow; size: 'sm' | 'lg' }) {
  const [errored, setErrored] = useState(false);
  const dim = size === 'sm' ? 'h-10 w-10' : 'h-32 w-32';
  const text = size === 'sm' ? 'text-[10px]' : 'text-2xl';
  const fallbackInitials = (row.canonicalName ?? row.sku).slice(0, 2).toUpperCase();
  // Stable swatch from SKU hash → consistent per-product colour.
  const swatch = ((): string => {
    const swatches = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-amber-100 text-amber-700',
      'bg-violet-100 text-violet-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    let h = 0;
    for (const c of row.sku) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return swatches[h % swatches.length];
  })();

  if (row.imageUrl && !errored) {
    return (
      <img
        src={row.imageUrl}
        alt={row.canonicalName ?? row.sku}
        onError={() => setErrored(true)}
        className={`${dim} rounded object-contain bg-white border border-slate-200 inline-flex`}
      />
    );
  }
  return (
    <div
      className={`${dim} ${swatch} rounded flex items-center justify-center font-semibold ${text} inline-flex`}
      title={row.canonicalName ?? row.sku}
    >
      {fallbackInitials}
    </div>
  );
}

interface CatalogDetailDrawerProps {
  row: CatalogRow;
  onClose: () => void;
}

function CatalogDetailDrawer({ row, onClose }: CatalogDetailDrawerProps) {
  // Default to "Preview" because that's the demo-impactful view — buyer sees
  // the customer-facing card first. Toggle to "Detail" for the JSON-y data
  // a partner would use when verifying enrichment quality.
  const [view, setView] = useState<'preview' | 'detail'>('preview');
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30" />
      <div
        className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-sm text-slate-900">{row.sku}</div>
            <div className="text-xs text-slate-500">
              {row.distributorLabel} · Approved {new Date(row.enrichedAt).toLocaleString()}
              {row.agentVersion ? ` · ${row.agentVersion}` : ''}
            </div>
          </div>
          <div className="inline-flex items-center gap-3">
            <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
              <button
                onClick={() => setView('preview')}
                className={`inline-flex px-2.5 py-1 text-xs rounded ${
                  view === 'preview'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Preview
              </button>
              <button
                onClick={() => setView('detail')}
                className={`inline-flex px-2.5 py-1 text-xs rounded ${
                  view === 'detail'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Detail
              </button>
            </div>
            <button
              onClick={onClose}
              className="inline-flex p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {view === 'preview' ? (
          <CustomerProductCard row={row} />
        ) : (
          <CatalogDetailView row={row} />
        )}
      </div>
    </div>
  );
}

/**
 * Mock of how this product would render in a typical reseller storefront —
 * what customers would see when browsing the partner's catalogue. Not wired
 * to any real storefront; lives here so the partner (and demo buyer) can
 * eyeball the end-customer experience before approving.
 */
function CustomerProductCard({ row }: { row: CatalogRow }) {
  const attributes = row.enrichment?.attributes ?? {};
  const attributeEntries = Object.entries(attributes).filter(([, v]) => v !== null && v !== '');
  return (
    <div className="bg-slate-50 min-h-full">
      <div className="m-6 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Hero image */}
        <div className="bg-white border-b border-slate-100 p-6 flex items-center justify-center min-h-[240px]">
          {row.imageUrl ? (
            <img
              src={row.imageUrl}
              alt={row.canonicalName ?? row.sku}
              className="max-h-56 max-w-full object-contain"
            />
          ) : (
            <ProductThumb row={row} size="lg" />
          )}
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {row.categoryPrimary && (
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {row.categoryPrimary}
              {row.categorySecondary && ` · ${row.categorySecondary}`}
            </div>
          )}
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">
            {row.canonicalName ?? row.sku}
          </h1>
          {row.marketingSummary && (
            <p className="text-sm text-slate-700 leading-relaxed">
              {row.marketingSummary}
            </p>
          )}

          {attributeEntries.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <div className="text-xs font-medium text-slate-700 mb-2">Specifications</div>
              <ul className="text-sm text-slate-700 space-y-1.5">
                {attributeEntries.slice(0, 8).map(([k, v]) => (
                  <li key={k} className="flex justify-between gap-3">
                    <span className="text-slate-500 capitalize">
                      {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim()}
                    </span>
                    <span className="font-medium text-slate-900 text-right">
                      {String(v)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-slate-100 pt-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-slate-500">Stock & price</div>
              <div className="text-sm text-slate-700 italic">
                Live from {row.distributorLabel} on quote
              </div>
            </div>
            <button
              type="button"
              disabled
              title="Cosmetic only — not wired to a cart"
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 disabled:opacity-90 disabled:cursor-not-allowed"
            >
              Add to quote
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 px-6 py-3 bg-slate-50 text-[11px] text-slate-500 inline-flex items-center justify-between w-full">
          <span>Sourced from {row.distributorLabel}</span>
          <span className="font-mono">{row.sku}</span>
        </div>
      </div>
      <p className="text-center text-[11px] text-slate-400 pb-6 px-6">
        Storefront preview — this is what a typical reseller customer card would render from this enrichment.
      </p>
    </div>
  );
}

function CatalogDetailView({ row }: { row: CatalogRow }) {
  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-center pb-2">
        <ProductThumb row={row} size="lg" />
      </div>
      <div>
        <div className="text-xs font-medium text-slate-700 mb-1">Canonical name</div>
        <div className="text-sm text-slate-900">
          {row.canonicalName ?? <span className="text-slate-400 italic">—</span>}
        </div>
      </div>

      <div>
        <div className="text-xs font-medium text-slate-700 mb-1">Marketing summary</div>
        <div className="text-sm text-slate-900 whitespace-pre-wrap">
          {row.marketingSummary ?? <span className="text-slate-400 italic">—</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Product type</div>
          <div className="text-sm text-slate-900">
            {row.productType ?? <span className="text-slate-400 italic">—</span>}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Category</div>
          <div className="text-sm text-slate-900">
            {row.categoryPrimary ?? '—'}
            {row.categorySecondary && (
              <span className="text-slate-500"> · {row.categorySecondary}</span>
            )}
          </div>
        </div>
      </div>

      {row.enrichment?.attributes && Object.keys(row.enrichment.attributes).length > 0 && (
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Attributes</div>
          <pre className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-[11px] text-slate-700 overflow-x-auto">
            {JSON.stringify(row.enrichment.attributes, null, 2)}
          </pre>
        </div>
      )}

      <div className="text-[11px] text-slate-500">
        Confidence: {row.confidence ?? '—'}
      </div>

      <div className="pt-4 border-t border-slate-200">
        <a
          href={`/marketplace?tab=distributors`}
          className="inline-flex text-xs text-blue-700 hover:text-blue-900 underline"
        >
          Edit / re-approve in {row.distributorLabel} tab →
        </a>
      </div>
    </div>
  );
}
